import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export interface GameSession {
  id: number
  email: string
  uuid: string
}

export interface GameResult {
  id: number
  gameName: string
  numberResult?: number
  jsonResult?: any
  jsonGameData?: any
}

export const useGameStore = defineStore('game', () => {
  const session = ref<GameSession | null>(null)
  const completedGames = ref<string[]>([])
  const gameProgress = ref<Record<string, number>>({})
  const results = ref<GameResult[]>([])
  const currentGameIndex = ref(0)
  
  const gameNames = ['game1', 'game2', 'game3', 'game4'] // Define your games here
  
  const currentGame = computed(() => gameNames[currentGameIndex.value])
  const isLastGame = computed(() => currentGameIndex.value >= gameNames.length - 1)
  const nextIncompleteGame = computed(() => {
    return gameNames.find(game => !completedGames.value.includes(game))
  })

  // API base URL - automatically detects environment
  const getApiBaseUrl = () => {
    if (typeof window !== 'undefined') {
      // In browser, use relative URLs which will be proxied by nginx
      return '/api'
    }
    // Fallback for server-side rendering
    return 'http://localhost:3001/api'
  }

  const createOrGetSession = async (email: string) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })
      
      if (!response.ok) {
        throw new Error('Failed to create session')
      }
      
      const data = await response.json()
      session.value = data.session
      completedGames.value = data.completedGames || []
      gameProgress.value = data.gameProgress || {}

      return data
    } catch (error) {
      console.error('Error creating session:', error)
      throw error
    }
  }

  const saveGameResult = async (gameName: string, numberResult?: number, jsonResult?: any, jsonGameData?: any, step?: number) => {
    if (!session.value) {
      throw new Error('No active session')
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/game-result`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: session.value.id,
          gameName,
          numberResult,
          jsonResult,
          jsonGameData,
          step,
        }),
      })
      
      if (!response.ok) {
        throw new Error('Failed to save game result')
      }
      
      const data = await response.json()
      
      // Update completed games logic will be handled differently now since we save by step
      return data
    } catch (error) {
      console.error('Error saving game result:', error)
      throw error
    }
  }

  const getSessionProgress = async () => {
    if (!session.value) {
      throw new Error('No active session')
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/session/${session.value.id}`)
      
      if (!response.ok) {
        throw new Error('Failed to get session progress')
      }
      
      const data = await response.json()
      completedGames.value = data.completedGames || []
      results.value = data.results || []
      
      return data
    } catch (error) {
      console.error('Error getting session progress:', error)
      throw error
    }
  }

  const goToNextGame = () => {
    if (currentGameIndex.value < gameNames.length - 1) {
      currentGameIndex.value++
    }
  }

  const setCurrentGame = (gameIndex: number) => {
    if (gameIndex >= 0 && gameIndex < gameNames.length) {
      currentGameIndex.value = gameIndex
    }
  }

  const getGameProgress = async (gameName: string) => {
    if (!session.value) {
      throw new Error('No active session')
    }

    try {
      const response = await fetch(`${getApiBaseUrl()}/session/${session.value.id}/game/${gameName}`)
      
      if (!response.ok) {
        throw new Error('Failed to get game progress')
      }
      
      const data = await response.json()
      return data.results || []
    } catch (error) {
      console.error('Error getting game progress:', error)
      throw error
    }
  }

  const reset = () => {
    session.value = null
    completedGames.value = []
    results.value = []
    currentGameIndex.value = 0
  }

  return {
    session,
    completedGames,
    gameProgress,
    results,
    currentGameIndex,
    gameNames,
    currentGame,
    isLastGame,
    nextIncompleteGame,
    createOrGetSession,
    saveGameResult,
    getSessionProgress,
    getGameProgress,
    goToNextGame,
    setCurrentGame,
    reset,
  }
})